import { Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import clinicInfo from '../../config/clinicInfo';
import { buildTenantPath } from '../../utils/tenantSlug';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import { useAuth } from '../../context/AuthContext';
import { buildTelHref } from '../../utils/contact';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { isAuthenticated } = useAuth();
  const { profile, effectiveTenantSlug, isTenantContext } = useClinicProfile();
  const tenantPath = (path: string) => buildTenantPath(path, effectiveTenantSlug);
  const tenantSummary =
    profile?.reportSubtitle ||
    'Atendimento especializado com foco em cuidado personalizado e acompanhamento proximo.';
  const contactPhoneDisplay = profile?.contactPhone || profile?.contactWhatsapp || '';
  const contactPhoneHref = buildTelHref(
    profile?.contactPhoneFull ||
    profile?.contactWhatsappFull ||
    profile?.contactPhone ||
    profile?.contactWhatsapp,
  );
  const footerTitle = isTenantContext ? (profile?.displayName || 'Clinica') : clinicInfo.nomeFantasia;
  const footerSubtitle = isTenantContext
    ? [profile?.displayName || 'Clinica', profile?.reportSubtitle].filter(Boolean).join(' - ')
    : `${clinicInfo.nomeFantasia} - ${clinicInfo.especialidades}`;
  const hasTenantAddress = Boolean(profile?.addressLine1 || profile?.addressLine2);
  const footerClasses = isAuthenticated
    ? 'bg-white text-neutral-900 border-t border-neutral-200'
    : 'bg-neutral-900 text-white';
  const secondaryTextClasses = isAuthenticated ? 'text-neutral-700' : 'text-neutral-300';
  const mutedTextClasses = isAuthenticated ? 'text-neutral-600' : 'text-neutral-400';
  const linkClasses = isAuthenticated
    ? 'text-neutral-700 hover:text-primary-700 transition-colors'
    : 'text-neutral-300 hover:text-primary-400 transition-colors';
  const iconClasses = isAuthenticated ? 'text-primary-600' : 'text-primary-400';
  const dividerClasses = isAuthenticated ? 'border-neutral-200' : 'border-neutral-800';

  return (
    <footer className={`${footerClasses} pt-16 pb-8 transition-colors duration-200`}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Column 1 - About */}
          <div>
            <div className="mb-4">
              {isTenantContext ? (
                profile?.logoWordmarkUrl ? (
                  <Link to={tenantPath("/")} className="inline-flex items-center">
                    <img
                      src={profile.logoWordmarkUrl}
                      alt={profile.logoAlt || footerTitle}
                      className="h-10 w-auto max-w-[240px] object-contain object-left"
                    />
                  </Link>
                ) : (
                  <Link
                    to={tenantPath("/")}
                    className={`text-2xl font-display font-bold ${isAuthenticated ? 'text-neutral-900' : 'text-white'}`}
                  >
                    {footerTitle}
                  </Link>
                )
              ) : (
                <Logo variant={isAuthenticated ? 'dark' : 'light'} />
              )}
            </div>
            <p className={`${secondaryTextClasses} mb-6`}>
              {isTenantContext
                ? tenantSummary
                : `Especialistas em ${clinicInfo.especialidades.toLowerCase()}, oferecendo tratamentos personalizados e atendimento humanizado.`}
            </p>
            {!isTenantContext && (
              <div className="flex space-x-4">
                <a
                  href={clinicInfo.redesSociais.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClasses}
                >
                  <Instagram size={20} />
                </a>
                <a
                  href={clinicInfo.redesSociais.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClasses}
                >
                  <Youtube size={20} />
                </a>
              </div>
            )}
          </div>

          {/* Column 2 - Links */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Links Rápidos</h3>
            <ul className="space-y-3">
              <li>
                <Link to={tenantPath("/")} className={linkClasses}>
                  Início
                </Link>
              </li>
              <li>
                <Link to={tenantPath("/sobre")} className={linkClasses}>
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link to={tenantPath("/servicos")} className={linkClasses}>
                  Serviços
                </Link>
              </li>
              <li>
                <Link to={tenantPath("/noticias")} className={linkClasses}>
                  Notícias
                </Link>
              </li>
              <li>
                <Link to={tenantPath("/cursos")} className={linkClasses}>
                  Cursos
                </Link>
              </li>
              <li>
                <Link to={tenantPath("/contato")} className={linkClasses}>
                  Contato
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 - Contact */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Contato</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin size={20} className={`${iconClasses} mr-3 mt-1 flex-shrink-0`} />
                <span className={secondaryTextClasses}>
                  {isTenantContext ? (
                    hasTenantAddress ? (
                      <>
                        {profile?.addressLine1}
                        {profile?.addressLine1 && profile?.addressLine2 ? <br /> : null}
                        {profile?.addressLine2}
                      </>
                    ) : (
                      'Endereco ainda nao configurado.'
                    )
                  ) : (
                    <>
                      {clinicInfo.endereco.logradouro}, {clinicInfo.endereco.numero}, {clinicInfo.endereco.complemento}<br />
                      {clinicInfo.endereco.bairro}, {clinicInfo.endereco.cidade} - {clinicInfo.endereco.estado}<br />
                      CEP: {clinicInfo.endereco.cep}
                    </>
                  )}
                </span>
              </li>
              <li className="flex items-center">
                <Phone size={20} className={`${iconClasses} mr-3 flex-shrink-0`} />
                {isTenantContext ? (
                  contactPhoneHref ? (
                    <a href={contactPhoneHref} className={linkClasses}>
                      {contactPhoneDisplay}
                    </a>
                  ) : (
                    <span className={secondaryTextClasses}>Telefone ainda nao configurado.</span>
                  )
                ) : (
                  <a href={`tel:${clinicInfo.contato.telefoneFull}`} className={linkClasses}>
                    {clinicInfo.contato.telefone}
                  </a>
                )}
              </li>
              <li className="flex items-center">
                <Mail size={20} className={`${iconClasses} mr-3 flex-shrink-0`} />
                {isTenantContext ? (
                  profile?.contactEmail ? (
                    <a href={`mailto:${profile.contactEmail}`} className={linkClasses}>
                      {profile.contactEmail}
                    </a>
                  ) : (
                    <span className={secondaryTextClasses}>Email ainda nao configurado.</span>
                  )
                ) : (
                  <a href={`mailto:${clinicInfo.contato.email}`} className={linkClasses}>
                    {clinicInfo.contato.email}
                  </a>
                )}
              </li>
            </ul>
          </div>

          {/* Column 4 - Hours */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Horário de Atendimento</h3>
            <ul className="space-y-2">
              {isTenantContext ? (
                <>
                  <li className={secondaryTextClasses}>Disponibilidade mediante agendamento.</li>
                  <li className={secondaryTextClasses}>Consulte horarios pelo WhatsApp ou e-mail.</li>
                </>
              ) : (
                <>
                  <li className="flex justify-between">
                    <span className={secondaryTextClasses}>Segunda - Sexta:</span>
                    <span className={secondaryTextClasses}>{clinicInfo.horarios.semana.split(': ')[1]}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className={secondaryTextClasses}>Sábado:</span>
                    <span className={secondaryTextClasses}>{clinicInfo.horarios.sabado.split(': ')[1]}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className={secondaryTextClasses}>Domingo:</span>
                    <span className={secondaryTextClasses}>{clinicInfo.horarios.domingo}</span>
                  </li>
                </>
              )}
            </ul>
            <div className="mt-6">
              <Link
                to={tenantPath("/agendar")}
                className="bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded-md transition-colors inline-block"
              >
                Agendar Consulta
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className={`pt-8 border-t ${dividerClasses} text-center`}>
          <p className={mutedTextClasses}>
            &copy; {currentYear} {footerSubtitle}. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
